import { useCallback, useRef, useState } from 'react';

export type ScannerFeedbackTone = 'success' | 'error';

/**
 * Haptic + audio beep + flash overlay state for scanner accept/reject feedback.
 */
export function useScannerFeedback() {
  const audioContextRef = useRef<AudioContext | null>(null);
  const [showFlash, setShowFlash] = useState(false);
  const [flashTone, setFlashTone] = useState<ScannerFeedbackTone>('success');

  const playBeep = useCallback((tone: ScannerFeedbackTone = 'success') => {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try {
        navigator.vibrate(tone === 'error' ? [40, 40, 40] : 80);
      } catch {
        // ignore haptic failures
      }
    }

    setFlashTone(tone);
    setShowFlash(true);
    setTimeout(() => setShowFlash(false), 350);

    if (typeof window === 'undefined') return;
    try {
      if (!audioContextRef.current) {
        type AudioContextConstructor = new () => AudioContext;
        const ctor = (window.AudioContext ||
          window.webkitAudioContext) as
          | AudioContextConstructor
          | undefined;
        if (!ctor) throw new Error('AudioContext unavailable');
        audioContextRef.current = new ctor();
      }
      const context = audioContextRef.current;
      if (context.state === 'suspended') void context.resume();

      const oscillator = context.createOscillator();
      const gainNode = context.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(context.destination);
      oscillator.frequency.value = tone === 'error' ? 220 : 980;
      gainNode.gain.value = 0.12;
      oscillator.start();
      oscillator.stop(context.currentTime + (tone === 'error' ? 0.18 : 0.12));
    } catch {
      // ignore audio failures
    }
  }, []);

  const disposeAudio = useCallback(() => {
    if (audioContextRef.current) {
      void audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  }, []);

  return { showFlash, flashTone, playBeep, disposeAudio };
}
